import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  STAGES_BY_FLOW_TYPE,
  stepTypeLabel,
  type FlowStepInput,
  type FlowStepType,
  type FlowType,
} from '../../types/collection-flow.type';
import {
  activateFlow,
  getActiveFlow,
  getFlowContract,
  listFlowStepTypes,
  saveFlowDraft,
} from '../../services/collection-flow.service';

interface Props {
  organizationId: string;
  flowType: FlowType;
  supplierId?: string;
  facilityId?: string;
}

interface BuilderStep extends FlowStepInput {
  step_type_key: string;
  semantic: string | null;
  kind: string;
}

/**
 * Composes a flow from the step catalogue: reorder with arrows (touch-reliable, no
 * drag dependency), toggle required, remove -- except core steps, which the activation
 * contract requires and which therefore render locked. Saving stores a draft;
 * Activate is contract-checked server-side and the required semantics are fetched
 * rather than restated, so the rule can never drift between client and server.
 */
export default function FlowBuilder({ organizationId, flowType, supplierId, facilityId }: Props) {
  const { t, i18n } = useTranslation();
  const language = i18n.language?.split('-')[0] ?? 'en';

  const [catalogue, setCatalogue] = useState<FlowStepType[]>([]);
  const [requiredSemantics, setRequiredSemantics] = useState<string[]>([]);
  const [anyOfGroups, setAnyOfGroups] = useState<string[][]>([]);
  const [steps, setSteps] = useState<BuilderStep[]>([]);
  const [activeVersion, setActiveVersion] = useState<number | null>(null);
  const [isDefault, setIsDefault] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerStage, setPickerStage] = useState<string>(STAGES_BY_FLOW_TYPE[flowType][0]);
  const [pickerKey, setPickerKey] = useState<string>('');

  const stages = STAGES_BY_FLOW_TYPE[flowType];
  const typesByKey = useMemo(
    () => new Map(catalogue.map((stepType) => [stepType.key, stepType])),
    [catalogue]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [types, contract, resolved] = await Promise.all([
        listFlowStepTypes(organizationId),
        getFlowContract(organizationId, flowType, facilityId),
        getActiveFlow(organizationId, flowType, { supplier_id: supplierId, facility_id: facilityId }),
      ]);
      setCatalogue(types.filter((stepType) => stepType.applies_to.includes(flowType)));
      setRequiredSemantics(contract.required_semantics);
      setAnyOfGroups(contract.any_of_semantics ?? []);
      setActiveVersion(resolved.version);
      setIsDefault(resolved.is_default);
      setSteps(
        resolved.steps.map((step) => ({
          step_key: step.step_key,
          step_type_key: (step as { step_type_key?: string }).step_type_key ?? step.step_key,
          stage: step.stage as BuilderStep['stage'],
          is_required: step.required,
          config: step.config ?? {},
          semantic: step.semantic,
          kind: step.kind,
        }))
      );
    } catch (loadError) {
      setError((loadError as Error)?.message ?? 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [organizationId, flowType, supplierId, facilityId]);

  useEffect(() => {
    void load();
  }, [load]);

  const helpFor = (typeKey: string): string | null => {
    const stepType = typesByKey.get(typeKey);
    const help = stepType?.help_text as Record<string, string> | null | undefined;
    return help?.[language] || help?.en || null;
  };

  const isCore = (step: BuilderStep) => {
    if (!step.semantic) return false;
    if (requiredSemantics.includes(step.semantic)) return true;
    // An any-of step (fill level / entered quantity) is locked only while it is the
    // group's sole required member -- add the alternative first and it unlocks.
    return anyOfGroups.some(
      (group) =>
        group.includes(step.semantic as string) &&
        step.is_required &&
        !steps.some(
          (other) =>
            other !== step &&
            other.is_required &&
            Boolean(other.semantic) &&
            group.includes(other.semantic as string)
        )
    );
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    [next[index], next[target]] = [next[target], next[index]];
    setSteps(next);
  };

  const remove = (index: number) => {
    setSteps(steps.filter((_, stepIndex) => stepIndex !== index));
  };

  const toggleRequired = (index: number) => {
    setSteps(
      steps.map((step, stepIndex) =>
        stepIndex === index ? { ...step, is_required: !step.is_required } : step
      )
    );
  };

  const addStep = () => {
    const stepType = typesByKey.get(pickerKey);
    if (!stepType) return;
    // A type can appear more than once per flow; each instance gets a unique key,
    // because answers are recorded under it.
    const taken = new Set(steps.map((step) => step.step_key));
    let instanceKey = stepType.key;
    for (let suffix = 2; taken.has(instanceKey); suffix += 1) {
      instanceKey = `${stepType.key}_${suffix}`;
    }
    setSteps([
      ...steps,
      {
        step_key: instanceKey,
        step_type_key: stepType.key,
        stage: pickerStage as BuilderStep['stage'],
        is_required: true,
        config: {},
        semantic: stepType.semantic,
        kind: stepType.kind,
      },
    ]);
    setPickerOpen(false);
    setPickerKey('');
  };

  const missingSemantics = requiredSemantics.filter(
    (semantic) => !steps.some((step) => step.semantic === semantic && step.is_required)
  );
  const missingAnyOf = anyOfGroups.filter(
    (group) =>
      !steps.some((step) => step.semantic && group.includes(step.semantic) && step.is_required)
  );
  const missingLabels = [
    ...missingSemantics,
    ...missingAnyOf.map((group) => group.join(' / ')),
  ];

  const persist = async (activate: boolean) => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const draft = await saveFlowDraft(organizationId, {
        flow_type: flowType,
        supplier_id: supplierId,
        facility_id: facilityId,
        steps: steps.map(({ step_key, step_type_key, stage, is_required, config }) => ({
          step_key,
          step_type_key,
          stage,
          is_required,
          config,
        })),
      });
      if (activate) {
        const activated = await activateFlow(organizationId, draft.id);
        setActiveVersion(activated.version);
        setIsDefault(false);
        setNotice(t('collectionFlow.activated'));
      } else {
        setNotice(t('collectionFlow.draftSaved'));
      }
    } catch (persistError) {
      const detail = (persistError as any)?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : detail?.message ?? (persistError as Error)?.message ?? 'Failed'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  // Every applicable type stays addable -- a type can be used more than once, so the
  // Add button never dead-ends just because the default flow uses the whole catalogue.
  const addable = catalogue;

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {isDefault ? (
          <Chip size="small" label={t('collectionFlow.usingDefault')} />
        ) : (
          <Chip size="small" color="success" icon={<CheckCircleIcon />} label={t('collectionFlow.activeVersion', { version: activeVersion })} />
        )}
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {notice ? <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert> : null}
      {missingLabels.length > 0 ? (
        <Alert severity="warning">
          {t('collectionFlow.missingCore')}{' '}
          {missingLabels.map((label) => (
            <Chip key={label} size="small" label={label} sx={{ mr: 0.5 }} />
          ))}
        </Alert>
      ) : null}

      {stages.map((stage) => (
        <Box key={stage}>
          <Typography variant="overline" color="text.secondary">
            {t(`collectionFlow.stages.${stage}`)}
          </Typography>
          <List dense disablePadding>
            {steps.map((step, index) => {
              if (step.stage !== stage) return null;
              const stepType = typesByKey.get(step.step_type_key);
              const locked = isCore(step);
              return (
                <ListItem
                  key={`${step.step_key}-${index}`}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 2, mb: 1 }}
                  secondaryAction={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {helpFor(step.step_type_key) ? (
                        <Tooltip title={helpFor(step.step_type_key) as string} arrow enterTouchDelay={0}>
                          <InfoOutlinedIcon fontSize="small" color="action" />
                        </Tooltip>
                      ) : null}
                      <Tooltip title={t('collectionFlow.required')}>
                        <Switch
                          size="small"
                          checked={step.is_required}
                          disabled={locked}
                          onChange={() => toggleRequired(index)}
                        />
                      </Tooltip>
                      <IconButton size="small" onClick={() => move(index, -1)} disabled={index === 0}>
                        <ArrowUpwardIcon fontSize="inherit" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => move(index, 1)}
                        disabled={index === steps.length - 1}
                      >
                        <ArrowDownwardIcon fontSize="inherit" />
                      </IconButton>
                      {locked ? (
                        <Tooltip title={t('collectionFlow.coreLocked')}>
                          <LockIcon fontSize="small" color="disabled" />
                        </Tooltip>
                      ) : (
                        <IconButton size="small" onClick={() => remove(index)}>
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      )}
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={stepType ? stepTypeLabel(stepType, language) : step.step_key}
                    secondary={`${step.kind}${step.semantic ? ` · ${step.semantic}` : ''}`}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>
      ))}

      <Stack direction="row" spacing={1}>
        <Button startIcon={<AddIcon />} onClick={() => setPickerOpen(true)} disabled={addable.length === 0}>
          {t('collectionFlow.addStep')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={() => void persist(false)} disabled={saving}>
          {t('collectionFlow.saveDraft')}
        </Button>
        <Tooltip title={missingLabels.length ? t('collectionFlow.missingCore') : ''}>
          <span>
            <Button
              variant="contained"
              onClick={() => void persist(true)}
              disabled={saving || missingLabels.length > 0 || steps.length === 0}
            >
              {t('collectionFlow.activate')}
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t('collectionFlow.addStep')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>{t('collectionFlow.step')}</InputLabel>
            <Select
              value={pickerKey}
              label={t('collectionFlow.step')}
              onChange={(event) => setPickerKey(event.target.value)}
            >
              {addable.map((stepType) => (
                <MenuItem key={stepType.key} value={stepType.key}>
                  {stepTypeLabel(stepType, language)} ({stepType.kind})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {pickerKey && helpFor(pickerKey) ? (
            <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />}>
              {helpFor(pickerKey)}
            </Alert>
          ) : null}
          <FormControl fullWidth>
            <InputLabel>{t('collectionFlow.stage')}</InputLabel>
            <Select
              value={pickerStage}
              label={t('collectionFlow.stage')}
              onChange={(event) => setPickerStage(event.target.value)}
            >
              {stages.map((stage) => (
                <MenuItem key={stage} value={stage}>
                  {t(`collectionFlow.stages.${stage}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={addStep} disabled={!pickerKey}>
            {t('collectionFlow.add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
